(() => {
  if (customElements.get('gvs-timeline-v2')) return;

  class GVSTimelineV2 extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-gvs-timeline-item]'));
      this.filters = Array.from(this.querySelectorAll('[data-gvs-timeline-filter]'));
      this.yearLinks = Array.from(this.querySelectorAll('[data-gvs-timeline-year-link]'));
      this.progress = this.querySelector('[data-gvs-timeline-progress]');
      this.floatingYear = this.querySelector('[data-gvs-timeline-floating-year]');
      this.enableProgress = this.dataset.enableProgress === 'true';
      this.enableFilters = this.dataset.enableFilters === 'true';
      this.enableFloatingYear = this.dataset.enableFloatingYear === 'true';
      this.enableReveal = this.dataset.enableReveal === 'true';
      this.enableCountUp = this.dataset.enableCountUp === 'true';
      this.countDuration = Math.max(0, Number(this.dataset.countDuration) || 1200);
      this.activeItem = null;
      this.ticking = false;
      this.itemObserver = null;
      this.countObserver = null;

      this.onFilterClick = this.onFilterClick.bind(this);
      this.onYearLinkClick = this.onYearLinkClick.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.update = this.update.bind(this);

      if (!this.items.length) return;

      this.classList.add('is-ready');
      if (!this.enableReveal) this.classList.add('is-reveal-disabled');

      this.filters.forEach((filter) => filter.addEventListener('click', this.onFilterClick));
      this.yearLinks.forEach((link) => link.addEventListener('click', this.onYearLinkClick));

      this.setupObservers();
      this.setupCountUp();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }

    disconnectedCallback() {
      this.filters.forEach((filter) => filter.removeEventListener('click', this.onFilterClick));
      this.yearLinks.forEach((link) => link.removeEventListener('click', this.onYearLinkClick));
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);

      if (this.itemObserver) this.itemObserver.disconnect();
      if (this.countObserver) this.countObserver.disconnect();
      if (this.frame) window.cancelAnimationFrame(this.frame);
    }

    setupObservers() {
      if (!('IntersectionObserver' in window)) {
        this.items.forEach((item) => item.classList.add('is-visible'));
        return;
      }

      this.itemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.setActiveItem(entry.target);
            }
          });
        },
        {
          root: null,
          rootMargin: '-38% 0px -48% 0px',
          threshold: 0
        }
      );

      this.items.forEach((item) => this.itemObserver.observe(item));
    }

    setupCountUp() {
      const counters = Array.from(this.querySelectorAll('[data-gvs-timeline-count]'));
      if (!counters.length) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!this.enableCountUp || reduceMotion || !('IntersectionObserver' in window)) {
        counters.forEach((counter) => this.setFinalCounterValue(counter));
        return;
      }

      this.countObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.animateCounter(entry.target);
            this.countObserver.unobserve(entry.target);
          });
        },
        { threshold: 0.45 }
      );

      counters.forEach((counter) => this.countObserver.observe(counter));
    }

    onFilterClick(event) {
      const filter = event.currentTarget;
      const category = filter.dataset.gvsTimelineFilter || 'all';

      this.filters.forEach((button) => {
        const isActive = button === filter;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.items.forEach((item) => {
        const itemCategory = item.dataset.gvsTimelineCategory || 'milestones';
        const shouldShow = category === 'all' || itemCategory === category;
        item.hidden = !shouldShow;
        item.classList.toggle('is-filter-hidden', !shouldShow);
      });

      this.yearLinks.forEach((link) => {
        const linkCategory = link.dataset.gvsTimelineCategory || 'milestones';
        const shouldShow = category === 'all' || linkCategory === category;
        link.hidden = !shouldShow;
      });

      const firstVisible = this.getVisibleItems()[0];
      if (firstVisible) this.setActiveItem(firstVisible);
      this.update();
    }

    onYearLinkClick(event) {
      const link = event.currentTarget;
      const targetId = link.dataset.gvsTimelineTarget;
      const target = targetId ? document.getElementById(targetId) : null;
      if (!target || target.hidden) return;

      event.preventDefault();

      const top = target.getBoundingClientRect().top + window.pageYOffset - 110;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: reduceMotion ? 'auto' : 'smooth'
      });

      if (window.history && window.history.pushState) {
        window.history.pushState(null, '', `#${target.id}`);
      }

      this.setActiveItem(target);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;

      window.requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      this.updateProgress();
      this.updateClosestActiveItem();
    }

    updateProgress() {
      if (!this.enableProgress || !this.progress) return;

      const body = this.querySelector('.gvs-timeline-v2__body');
      if (!body) return;

      const rect = body.getBoundingClientRect();
      const viewportAnchor = window.innerHeight * 0.42;
      const progress = (viewportAnchor - rect.top) / Math.max(1, rect.height - viewportAnchor);
      const clamped = Math.min(1, Math.max(0, progress));

      this.style.setProperty('--gvs-timeline-progress', `${clamped * 100}%`);
    }

    updateClosestActiveItem() {
      const visibleItems = this.getVisibleItems();
      if (!visibleItems.length) return;

      const anchor = window.innerHeight * 0.45;
      let closest = visibleItems[0];
      let closestDistance = Number.POSITIVE_INFINITY;

      visibleItems.forEach((item) => {
        const rect = item.getBoundingClientRect();
        const itemCentre = rect.top + rect.height * 0.35;
        const distance = Math.abs(itemCentre - anchor);

        if (distance < closestDistance) {
          closest = item;
          closestDistance = distance;
        }
      });

      this.setActiveItem(closest);
    }

    setActiveItem(item) {
      if (!item || item.hidden || this.activeItem === item) return;

      this.activeItem = item;
      const year = item.dataset.gvsTimelineYear || '';
      const id = item.id;

      this.items.forEach((timelineItem) => {
        timelineItem.classList.toggle('is-active', timelineItem === item);
      });

      this.yearLinks.forEach((link) => {
        const isActive = link.dataset.gvsTimelineTarget === id;
        link.classList.toggle('is-active', isActive);
        if (isActive) {
          link.setAttribute('aria-current', 'true');
          this.keepLinkInView(link);
        } else {
          link.removeAttribute('aria-current');
        }
      });

      if (this.enableFloatingYear && this.floatingYear && year) {
        this.floatingYear.textContent = year;
        this.floatingYear.classList.add('is-visible');
      }
    }

    getVisibleItems() {
      return this.items.filter((item) => !item.hidden);
    }

    keepLinkInView(link) {
      const container = link.closest('.gvs-timeline-v2__year-nav');
      if (!container || window.matchMedia('(min-width: 750px)').matches) return;

      const containerRect = container.getBoundingClientRect();
      const linkRect = link.getBoundingClientRect();
      const outOfView = linkRect.left < containerRect.left || linkRect.right > containerRect.right;

      if (outOfView) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const targetLeft = container.scrollLeft
          + (linkRect.left - containerRect.left)
          - ((container.clientWidth - linkRect.width) / 2);

        container.scrollTo({
          left: Math.max(0, targetLeft),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      }
    }

    getCounterParts(value) {
      const raw = String(value || '').trim();
      const match = raw.match(/(-?\d[\d,]*(?:\.\d+)?)/);

      if (!match) {
        return { raw, number: null, prefix: '', suffix: '', decimals: 0 };
      }

      const numericText = match[1];
      const number = Number(numericText.replace(/,/g, ''));
      const prefix = raw.slice(0, match.index);
      const suffix = raw.slice(match.index + numericText.length);
      const decimals = numericText.includes('.') ? numericText.split('.')[1].length : 0;

      return { raw, number, prefix, suffix, decimals };
    }

    formatCounter(value, parts) {
      if (parts.number === null || Number.isNaN(parts.number)) return parts.raw;

      const formatted = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: parts.decimals,
        maximumFractionDigits: parts.decimals
      }).format(value);

      return `${parts.prefix}${formatted}${parts.suffix}`;
    }

    setFinalCounterValue(counter) {
      const parts = this.getCounterParts(counter.dataset.target || counter.textContent);
      counter.textContent = parts.raw;
    }

    animateCounter(counter) {
      if (counter.dataset.hasAnimated === 'true') return;
      counter.dataset.hasAnimated = 'true';

      const parts = this.getCounterParts(counter.dataset.target || counter.textContent);
      if (parts.number === null || Number.isNaN(parts.number)) {
        counter.textContent = parts.raw;
        return;
      }

      const start = 0;
      const end = parts.number;
      const duration = this.countDuration;
      const startTime = performance.now();

      const tick = (time) => {
        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const current = start + (end - start) * eased;

        counter.textContent = this.formatCounter(current, parts);

        if (progress < 1) {
          this.frame = window.requestAnimationFrame(tick);
        } else {
          counter.textContent = parts.raw;
        }
      };

      counter.textContent = this.formatCounter(start, parts);
      this.frame = window.requestAnimationFrame(tick);
    }
  }

  customElements.define('gvs-timeline-v2', GVSTimelineV2);
})();